import { Button } from '@heidi/design-system/button';
import {
  DialogDrawer,
  DialogDrawerContent,
  DialogDrawerTitle,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import { useTranslations } from '@heidi/runtime/i18n';
import { useAtom } from 'jotai';
import { Check, ExternalLinkIcon, LoaderCircle } from 'lucide-react';
import Link from 'next/link';
import { type ReactNode, useEffect, useState } from 'react';

import {
  MOBILE_APP_DOWNLOAD_SCREEN,
  type TrackMobileAppDownloadModalViewed,
} from '../../contracts/mobile-app-download-analytics.types';
import { isGetMobileAppModalOpenAtom } from './atoms';
import { MobileAppQrCode } from './MobileAppQrCode';

export type GetMobileAppModalProps = {
  /**
   * The brand mark above the title. A slot rather than an import: the asset is
   * `@/assets/…` and rendering it needs `next/image`, which this zone does not
   * admit.
   */
  logo: ReactNode;
  /**
   * The absolute `/get-mobile` URL the QR code encodes. Built app-side, where
   * `router.basePath` lives — the route never crosses.
   */
  mobileLinkHref: string;
  /**
   * Whether the email CTA is offered, read at RENDER TIME rather than taken as
   * a boolean: it resolves through PostHog, and a resolved boolean would freeze
   * the flip at the first render. The nullable return is the getter's own.
   */
  isSendEmailEnabled: () => boolean | undefined;
  /** Sends the download link to the signed-in user's email. */
  sendLink: (opts: { onSuccess: () => void }) => void;
  /** Whether `sendLink` is in flight. */
  isSendingLink: boolean;
  /**
   * The `modal_viewed` emit, fired from the open-state effect below — this is
   * where the open→closed→open decision lives — and bound by the app. It is
   * read as an effect dependency, so it must be referentially stable or the
   * event fires again on every render while the modal is open.
   */
  onModalViewed: TrackMobileAppDownloadModalViewed;
};

export function GetMobileAppModal({
  logo,
  mobileLinkHref,
  isSendEmailEnabled,
  sendLink,
  isSendingLink,
  onModalViewed,
}: GetMobileAppModalProps) {
  const { t } = useTranslations();
  const [emailSent, setEmailSent] = useState(false);

  const handleSendLink = () => {
    sendLink({
      onSuccess: () => {
        setEmailSent(true);
      },
    });
  };

  const [isOpen, setIsOpen] = useAtom(isGetMobileAppModalOpenAtom);

  // Reset emailSent state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setEmailSent(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      onModalViewed({
        screen_name: MOBILE_APP_DOWNLOAD_SCREEN.getHeidiMobile,
      });
    }
  }, [isOpen, onModalViewed]);

  return (
    <DialogDrawer open={isOpen} onOpenChange={setIsOpen}>
      <DialogDrawerContent className="flex w-full max-w-md flex-col items-center gap-8 p-4 sm:w-11/12 sm:p-8">
        <div className="flex flex-col items-center gap-4">
          {logo}
          <DialogDrawerTitle>{t('mobileAppModal.scanToGetHeidi')}</DialogDrawerTitle>
        </div>

        {/* QR Code Section */}
        {/* !bg-white overrides dark mode background so the QR code stays scannable */}
        <MobileAppQrCode
          className="rounded-lg border border-border !bg-white p-6 shadow-md"
          size={224}
          value={mobileLinkHref}
        />

        {/* action buttons */}
        <div className="flex w-full flex-col gap-3">
          {isSendEmailEnabled() && (
            <Button
              variant="default"
              size="touch"
              className="w-full"
              onClick={handleSendLink}
              disabled={isSendingLink || emailSent}
            >
              {emailSent && <Check className="size-5" />}
              {isSendingLink
                ? t('mobileAppModal.sendingLink')
                : emailSent
                  ? t('mobileAppModal.sentToEmail')
                  : t('mobileAppModal.sendLinkNow')}
              {isSendingLink && <LoaderCircle className="size-5 animate-spin" />}
            </Button>
          )}
          <Link
            target="_blank"
            href="https://apps.apple.com/au/app/heidi-ai-medical-scribe/id6504471106#information"
          >
            <Button variant="outline" size="touch" className="w-full">
              {t('mobileAppModal.openInAppStore')}
              <ExternalLinkIcon />
            </Button>
          </Link>
          <Button
            variant="ghost"
            size="touch"
            className="w-full"
            onClick={() => setIsOpen(false)}
          >
            {t('mobileAppModal.close')}
          </Button>
        </div>
      </DialogDrawerContent>
    </DialogDrawer>
  );
}
